import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useSelector } from 'react-redux'

import ButtonRemove from 'components/ButtonRemove'
import ButtonRounded from 'components/ButtonRounded'
import ControlAmount from 'components/ControlAmount'
import { GOODS_TYPES } from 'constants/sales'
import { ResizeContext } from 'contexts/Resize'
import { getSales } from 'selectors'

import CartCostBlock from '../CartCostBlock'

import SubscriptionToggler from './components/SubscriptionToggler'
import {
  CartProductBlock,
  DynamicImageStyled,
  CostBlock,
  TextCartEmpty,
  ButtonBlockDesktop,
  ButtonBlockMobile,
} from './styles'

/**
 * CartProduct
 *
 * @param {object} props - properties of component
 * @returns {node} CartProduct
 */
const CartProduct = props => {
  const {
    boxes,
    currencyCode,
    dict,
    productInfo,
    productConfig,
    productType,
    handleIncrease,
    handleDecrease,
    toggleSubscription,
    intlCode,
    titleLink,
    texts,
    discount,
    discountLabel,
    isDisabled,
    isCartInitOrUpdate,
    precision,
    isСontrolsHidden,
    isSpecial,
  } = props
  const { isSubscription } = productInfo
  const { breakpoint, mobile, tablet } = useContext(ResizeContext)
  const sales = useSelector(getSales)
  const handleAddProduct = () =>
    handleIncrease({
      productType,
      isSubscription: false,
    })
  const handleIncreaseCurrent = () =>
    handleIncrease({
      productType,
      isSubscription,
    })
  const handleDecreaseCurrent = () =>
    handleDecrease({
      productType,
      isSubscription,
    })

  const isEmpty = productInfo.amount === 0
  const isZeroPrice =
    sales?.goods[productType]?.price === 0 ||
    sales?.goods[productType]?.priceWebsite === 0 ||
    sales?.goods[productType]?.currentPrice === 0

  const imageContent = (
    <DynamicImageStyled
      image={boxes}
      imageSizes={{ md: 160 }}
      imageType="png"
      alt=""
    />
  )
  const afterTitleContent =
    !isEmpty &&
    !isСontrolsHidden &&
    productConfig.subscription &&
    productConfig.subscription.price ? (
      <SubscriptionToggler
        productType={productType}
        toggleSubscription={toggleSubscription}
        dict={dict}
        productInfo={productInfo}
        breakpoint={breakpoint}
        mobile={mobile}
        tablet={tablet}
        isDisabled={isDisabled}
      />
    ) : null
  let descrBlockContent = null

  if (isEmpty) {
    descrBlockContent = (
      <TextCartEmpty
        dangerouslySetInnerHTML={{
          __html: texts.products[productType].content,
        }}
      />
    )
  } else if (!isСontrolsHidden) {
    descrBlockContent = isSubscription ? (
      <ButtonRemove
        isDisabled={isDisabled}
        onClick={handleDecreaseCurrent}
        title={texts.labels.remove_product}
      />
    ) : (
      <ControlAmount
        isActive={!isDisabled}
        amount={productInfo.amount}
        isWhite={isSpecial}
        onMinusClick={handleDecreaseCurrent}
        onPlusClick={handleIncreaseCurrent}
        description={mobile || !breakpoint ? '' : dict.kits.amount}
      />
    )
  }

  const rightBlockContent = isEmpty ? (
    <>
      <ButtonBlockMobile>
        <ButtonRounded
          isDisabled={isDisabled || isZeroPrice}
          as="button"
          iconStrokeWidth="2"
          wrapperProps={{
            onClick: handleAddProduct,
          }}
        >
          {isZeroPrice ? dict.buttons.out_of_stock : dict.buttons.add_to_cart}
        </ButtonRounded>
      </ButtonBlockMobile>
      <ButtonBlockDesktop>
        <ButtonRounded
          isDisabled={isDisabled || isZeroPrice}
          as="button"
          icon="btn_plus"
          iconStrokeWidth="2"
          wrapperProps={{
            onClick: handleAddProduct,
          }}
        >
          {isZeroPrice ? dict.buttons.out_of_stock : dict.buttons.add_to_cart}
        </ButtonRounded>
      </ButtonBlockDesktop>
    </>
  ) : (
    <CostBlock>
      <CartCostBlock
        key={`key-${intlCode}-${currencyCode}`}
        intlCode={intlCode}
        currencyCode={currencyCode}
        dictForKit={dict.kits[productType]}
        dictCart={texts}
        cost={productInfo.cost}
        realCost={isSubscription ? productConfig.price : productInfo.realCost}
        discount={productConfig.discount}
        precision={precision}
        showDiscount={!isSubscription}
        showCrossedCost={!!productConfig.discount || !!isSubscription}
        isSubscription={isSubscription}
        isLoading={isCartInitOrUpdate}
      />
    </CostBlock>
  )

  return (
    <CartProductBlock
      isSubscription={isSubscription}
      isEmpty={isEmpty}
      imageContent={imageContent}
      title={dict.kits[productType].title}
      subtitle={
        isEmpty && productType === GOODS_TYPES.dna
          ? {}
          : texts.products[productType].subtitle
      }
      discount={isEmpty && !isZeroPrice ? discount : 0}
      discountLabel={isZeroPrice ? null : discountLabel}
      afterTitleContent={afterTitleContent}
      titleLink={titleLink}
      descrBlockContent={descrBlockContent}
      rightBlockContent={rightBlockContent}
      isSpecial={isSpecial}
      alignByCenter={true}
      isMobile={mobile || tablet}
      label={texts.special_offer}
    />
  )
}

CartProduct.propTypes = {
  boxes: PropTypes.string.isRequired,
  currencyCode: PropTypes.string.isRequired,
  dict: PropTypes.object.isRequired,
  handleDecrease: PropTypes.func.isRequired,
  handleIncrease: PropTypes.func.isRequired,
  intlCode: PropTypes.string.isRequired,
  productConfig: PropTypes.object.isRequired,
  productInfo: PropTypes.object.isRequired,
  productType: PropTypes.string.isRequired,
  texts: PropTypes.object.isRequired,
  discount: PropTypes.number,
  discountLabel: PropTypes.string,
  titleLink: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isCartInitOrUpdate: PropTypes.bool.isRequired,
  toggleSubscription: PropTypes.func,
  precision: PropTypes.number,
  isСontrolsHidden: PropTypes.bool,
  isSpecial: PropTypes.bool.isRequired,
}

CartProduct.defaultProps = {
  isEmpty: true,
  isСontrolsHidden: false,
  isSpecial: false,
  discount: 0,
}

export default CartProduct
